import React, { useEffect, useState } from "react";
import { useTheme, useMediaQuery, Box, Stack, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import axios from "axios";

const colorPalette = ["#FE195F", "#00C7F1", "#FDB411", "#4CAF50","#9C27B0", "#3F51B5", "#E91E63"];
const leadSourceColors = {}; 

export default function SalesPieChart() {

  let branch_code_from_local_storage = localStorage.getItem("currentSelectedDepartment");
  let company_code_from_local_storage = localStorage.getItem("companyCode") ? localStorage.getItem("companyCode") : "-"
  let apiPaths_from_local_storage = localStorage.getItem("apiPaths") ? JSON.parse( localStorage.getItem("apiPaths") ) : "-"

  const [convertedLeadsData, setConvertedLeadsData] = useState(null);
  const [totalLeadsData, setTotalLeadsData] = useState(null);
  const [conversionRateData, setConversionRateData] = useState(null);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Ensure two digits for month
    const day = String(today.getDate()).padStart(2, "0"); // Ensure two digits for day
    return `${year}-${month}-${day}`;
  };
  
  const todayDate = getTodayDate();

  useEffect(() => {
    fetchChartData();
  }, []);

  const fetchChartData = async () => {
    try {
      const response = await axios.get(
        `${apiPaths_from_local_storage[0].API_CONFIG}/api/HRDashboard/${company_code_from_local_storage}/${branch_code_from_local_storage}/CRM_LEAD_CONVERSION/2024-01-01/${todayDate}/-/-/-/-/-`
      );

      const formattedTotalData = formatPieChartData(response.data, "TotalLeads");
      const formattedConvertedData = formatConvertedLeads(response.data);

      setTotalLeadsData(formattedTotalData);
      setConvertedLeadsData(formattedConvertedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Assign consistent colors to each lead source
  const getLeadSourceColor = (leadSource) => {
    if (!leadSourceColors[leadSource]) {
      const colorIndex = Object.keys(leadSourceColors).length % colorPalette.length;
      leadSourceColors[leadSource] = colorPalette[colorIndex];
    }
    return leadSourceColors[leadSource];
  };

  // Format Total Leads Data (Outer Donut)
  const formatPieChartData = (data, key) => {
    return data?.map((item, index) => ({
      id: index,
      value: item[key],
      label: item.lead_source || "Unknown Source",
      color: getLeadSourceColor(item.lead_source || "Unknown Source"),
      rate: item.ConversionRate || 0
    }));
  };


  const lightenColor = (color, percent) => {
    let num = parseInt(color.replace("#", ""), 16),
      amt = Math.round(2.55 * percent),
      r = (num >> 16) + amt,
      g = ((num >> 8) & 0x00ff) + amt,
      b = (num & 0x0000ff) + amt;
  
    return `#${(0x1000000 + 
      (r < 255 ? (r < 1 ? 0 : r) : 255) * 0x10000 +
      (g < 255 ? (g < 1 ? 0 : g) : 255) * 0x100 +
      (b < 255 ? (b < 1 ? 0 : b) : 255))
      .toString(16)
      .slice(1)}`;
  };

  // Format Converted Leads Data (Inner Donut: Converted + Remaining)
  const formatConvertedLeads = (data) => {
    return data?.flatMap((item, index) => {
      const conversionRate = item.ConversionRate || 0
      setConversionRateData(conversionRate) 
      const leadSource = item.lead_source || "Unknown Source";
      const totalLeads = item.TotalLeads || 0;
      const convertedLeads = item.ConvertedLeads || 0;
      const remainingLeads = totalLeads - convertedLeads;
      const assignedColor = getLeadSourceColor(leadSource);
      const lighterColor = lightenColor(assignedColor, 40);

      return [
        { id: `${index}-converted`, value: convertedLeads, label: `${leadSource} (Converted ${conversionRate}%)`, color: assignedColor },
        { id: `${index}-remaining`, value: remainingLeads, label: `${leadSource} (Unconverted)`, color: lighterColor }, // Gray for remaining leads
      ];
    });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // xs or sm

  const chartWidth = isMobile ? 300 : 400;
  const chartHeight = isMobile ? 250 : 300;

  return (
 <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={4}
      sx={{ width: "100%", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}
    >
      <Box sx={{ flexGrow: 1, width: "100%", maxWidth: 500 , paddingLeft:"20px"}}>
        <h2 className="text-lg text-center font-semibold mb-2">LEAD CONVERSION RATE</h2>
        {convertedLeadsData && totalLeadsData ? (
          <PieChart
            series={[
              {
                id: "ConvertedLeads",
                data: convertedLeadsData,
                innerRadius: 80,
                outerRadius: 120,
                arcLabel: (item) => (item.value > 0 ? `${item.value}` : ""),
                colors: convertedLeadsData.map((d) => d.color),
              },
              {
                id: "TotalLeads",
                data: totalLeadsData,
                innerRadius: 40,
                outerRadius: 80,
                arcLabel: (item) => `${item.value}`,
                colors: totalLeadsData.map((d) => d.color),
              },
            ]}
            width={chartWidth}
            height={chartHeight}
            slotProps={{ legend: { hidden: true } }}
          />
        ) : (
          <Typography textAlign="center">Loading chart...</Typography>
        )}
      </Box>
    </Stack>
  );
}